import React from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import Navbar from '../shared/Navbar';
import { LearningNav, HowItWorks, LearningFooter } from './LearningPageSections';

type LevelCard = {
  title: string;
  blurb: string;
  value: string;
  icon: string;
  route?: string;
  catalogue?: boolean;
  disabled?: boolean;
};

type SubjectCard = {
  title: string;
  blurb: string;
  value: string;
  icon: string;
  levels: { label: string; live?: boolean }[];
};

const levelGroups: { label: string; levels: LevelCard[] }[] = [
  {
    label: 'Structured exam paths',
    levels: [
      { title: '11+', blurb: 'Selective school entrance preparation', value: 'Eleven Plus', icon: 'i-graduation-cap', route: '/11-plus' },
      { title: 'GCSE', blurb: 'Foundation and higher tier syllabuses', value: 'GCSE / High School', icon: 'i-library', route: '/gcse' },
      { title: 'A Level', blurb: 'Sixth-form depth and exam technique', value: 'A Level', icon: 'i-target', disabled: true },
    ],
  },
  {
    label: 'Explore beyond school',
    levels: [
      { title: 'University (UG + PG)', blurb: 'Browse undergrad and postgrad topics by area', value: 'University', icon: 'i-landmark', catalogue: true },
      { title: 'General', blurb: 'Browse open-ended courses not tied to one exam', value: 'General', icon: 'i-brain', catalogue: true },
      { title: 'Professional Development', blurb: 'Browse workplace skills courses by topic', value: 'Professional Development', icon: 'i-briefcase', catalogue: true },
    ],
  },
];

const subjects: SubjectCard[] = [
  {
    title: 'English Literature',
    blurb: 'Close reading, criticism and craft',
    value: 'English Literature',
    icon: 'i-feather',
    levels: [{ label: 'GCSE', live: true }, { label: 'General' }],
  },
  {
    title: 'Italian',
    blurb: 'From beginner to fluent conversation',
    value: 'Italian Language',
    icon: 'i-languages',
    levels: [{ label: 'General' }],
  },
  {
    title: 'Maths',
    blurb: 'From arithmetic to abstract algebra',
    value: 'Mathematics',
    icon: 'i-calculator',
    levels: [{ label: 'GCSE', live: true }, { label: 'University' }],
  },
  {
    title: 'Science',
    blurb: 'Physics, Chemistry and Biology',
    value: 'Science',
    icon: 'i-atom',
    levels: [{ label: 'GCSE', live: true }, { label: 'University' }],
  },
  {
    title: 'Economics',
    blurb: 'Micro, macro and quantitative methods',
    value: 'Economics',
    icon: 'i-trending-up',
    levels: [{ label: 'University' }],
  },
  {
    title: 'Finance',
    blurb: 'Markets, valuation and financial decision-making',
    value: 'Finance',
    icon: 'i-trending-up',
    levels: [{ label: 'Pro Dev' }],
  },
  {
    title: 'Computer Science',
    blurb: 'Algorithms, theory and modern systems',
    value: 'Computer Science',
    icon: 'i-code-xml',
    levels: [{ label: 'University' }],
  },
];

const browseUrl = (key: 'subject' | 'level', value: string) => `/browse?${key}=${encodeURIComponent(value)}`;

export default function SubjectsPage() {
  return (
    <div className="lp-root learning-page subjects-page">
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <symbol id="i-arrow-right" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
          </symbol>
          <symbol id="i-arrow-left" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 19-7-7 7-7" /><path d="M19 12H5" />
          </symbol>
          <symbol id="i-graduation-cap" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z" />
            <path d="M22 10v6" /><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" />
          </symbol>
          <symbol id="i-library" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m16 6 4 14" /><path d="M12 6v14" /><path d="M8 8v12" /><path d="M4 4v16" />
          </symbol>
          <symbol id="i-target" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
          </symbol>
          <symbol id="i-landmark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" x2="21" y1="22" y2="22" /><line x1="6" x2="6" y1="18" y2="11" />
            <line x1="10" x2="10" y1="18" y2="11" /><line x1="14" x2="14" y1="18" y2="11" />
            <line x1="18" x2="18" y1="18" y2="11" /><polygon points="12 2 20 7 4 7" />
          </symbol>
          <symbol id="i-brain" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
            <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
          </symbol>
          <symbol id="i-briefcase" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /><rect width="20" height="14" x="2" y="6" rx="2" />
          </symbol>
          <symbol id="i-feather" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.67 19a2 2 0 0 0 1.416-.588l6.154-6.172a6 6 0 0 0-8.49-8.49L5.586 9.914A2 2 0 0 0 5 11.328V18a1 1 0 0 0 1 1z" />
            <path d="M16 8 2 22" /><path d="M17.5 15H9" />
          </symbol>
          <symbol id="i-languages" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 8 6 6" /><path d="m4 14 6-6 2-3" /><path d="M2 5h12" /><path d="M7 2h1" />
            <path d="m22 22-5-10-5 10" /><path d="M14 18h6" />
          </symbol>
          <symbol id="i-calculator" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="16" height="20" x="4" y="2" rx="2" /><line x1="8" x2="16" y1="6" y2="6" />
            <line x1="16" x2="16" y1="14" y2="18" /><path d="M16 10h.01" /><path d="M12 10h.01" /><path d="M8 10h.01" />
            <path d="M12 14h.01" /><path d="M8 14h.01" /><path d="M12 18h.01" /><path d="M8 18h.01" />
          </symbol>
          <symbol id="i-atom" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="1" />
            <path d="M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9-4.54-4.52-9.87-6.54-11.9-4.5-2.04 2.03-.02 7.36 4.5 11.9 4.54 4.52 9.87 6.54 11.9 4.5Z" />
            <path d="M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9-2.03-2.04-7.36-.02-11.9 4.5-4.52 4.54-6.54 9.87-4.5 11.9 2.03 2.04 7.36.02 11.9-4.5Z" />
          </symbol>
          <symbol id="i-trending-up" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
          </symbol>
          <symbol id="i-code-xml" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m18 16 4-4-4-4" /><path d="m6 8-4 4 4 4" /><path d="m14.5 4-5 16" />
          </symbol>
        </defs>
      </svg>

      <Navbar />
      <LearningNav />

      <main id="top">
        <section id="choose" className="block">
          <div className="container">
            <div className="section-head">
              <h2>Choose your level</h2>
              <p>Pick the stage you&rsquo;re studying at.</p>
            </div>

            {levelGroups.map(group => (
              <div className="level-group" key={group.label}>
                <p className="level-group-label">{group.label}</p>
                <div className="grid-levels stagger">
                  {group.levels.map(level => {
                    const cardContent = (
                      <>
                        <span className="icon-square">
                          <svg className="icon-lg" aria-hidden="true"><use href={`#${level.icon}`} /></svg>
                        </span>
                        <div className="card-body">
                          <div className="card-row">
                            <h3>{level.title}</h3>
                            {level.disabled ? (
                              <span className="card-status">Coming soon</span>
                            ) : (
                              <svg className="icon-md arrow" aria-hidden="true"><use href="#i-arrow-right" /></svg>
                            )}
                          </div>
                          <p className="card-blurb">{level.blurb}</p>
                        </div>
                      </>
                    );

                    if (level.disabled) {
                      return (
                        <div className="card-level disabled" key={level.value} aria-disabled="true">
                          {cardContent}
                        </div>
                      );
                    }

                    return (
                      <Link
                        className={`card-level${level.catalogue ? ' catalogue' : ''}`}
                        to={level.route ?? browseUrl('level', level.value)}
                        key={level.value}
                      >
                        {cardContent}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="block landing-subjects">
          <div className="container">
            <div className="section-head">
              <h2>Or jump in by subject</h2>
              <p>Prefer to start with a subject?</p>
            </div>

            <div className="grid-subjects stagger">
              {subjects.map(subject => (
                <Link className="card-subject" to={browseUrl('subject', subject.value)} key={subject.value}>
                  <span className="icon-blob">
                    <svg className="icon-xl" aria-hidden="true"><use href={`#${subject.icon}`} /></svg>
                  </span>
                  <h3>{subject.title}</h3>
                  <p>{subject.blurb}</p>
                  <div className="subject-levels">
                    {subject.levels.map(level => (
                      <span className={`subject-level-pill${level.live ? ' is-live' : ''}`} key={level.label}>
                        {level.label}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="closing">
          <div className="closing-inner">
            <h2>Not sure where to start?</h2>
            <p>Browse the full catalogue or pick one of the paths above.</p>
            <Link className="tm-button tm-button--primary btn btn-dark" to="/browse">
              See all courses
              <svg className="icon-md" aria-hidden="true"><use href="#i-arrow-right" /></svg>
            </Link>
          </div>
        </section>
        <HowItWorks />
      </main>

      <LearningFooter />
    </div>
  );
}
